// Mailbox providers our leads use most. A domain one typo away from one of
// them gets a "Meinten Sie …?" suggestion under the email field.
const KNOWN_DOMAINS = [
  "gmx.de",
  "gmx.net",
  "gmx.at",
  "gmx.ch",
  "gmx.com",
  "web.de",
  "t-online.de",
  "freenet.de",
  "online.de",
  "arcor.de",
  "mail.de",
  "email.de",
  "posteo.de",
  "posteo.net",
  "gmail.com",
  "googlemail.com",
  "yahoo.de",
  "yahoo.com",
  "hotmail.de",
  "hotmail.com",
  "outlook.de",
  "outlook.com",
  "live.de",
  "live.com",
  "icloud.com",
  "me.com",
  "aol.com",
  "aol.de",
  "bluewin.ch",
  "aon.at"
];

// Domains people often type that are not the provider's mailbox domain
const MISTAKEN_DOMAINS = {
  "gmail.de": "gmail.com",
  "googlemail.de": "googlemail.com",
  "icloud.de": "icloud.com",
  "t-online.com": "t-online.de"
};

// Endings that are no top-level domain at all, only typos of one. Like the
// former ".con" check these block the submit until the address is fixed.
const TLD_TYPOS = {
  con: "com",
  cmo: "com",
  ocm: "com",
  vom: "com",
  xom: "com",
  comm: "com",
  coom: "com",
  dee: "de",
  ded: "de",
  ed: "de",
  nett: "net",
  ner: "net",
  nte: "net"
};

// Edit distance where swapping two neighbouring letters counts as one typo
const editDistance = (a, b) => {
  const d = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) =>
      i === 0 ? j : j === 0 ? i : 0
    )
  );
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + cost
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
};

/**
 * Suggests a corrected address for common domain typos.
 * Returns { email, blocking } or null; `blocking` is true when the ending
 * is no real top-level domain (e.g. ".con"), so the address cannot work.
 */
export const getEmailSuggestion = (email) => {
  const value = (email || "").trim();
  const atIndex = value.lastIndexOf("@");
  if (atIndex < 1) return null;

  const localPart = value.slice(0, atIndex);
  const domain = value.slice(atIndex + 1).toLowerCase();
  const dotIndex = domain.lastIndexOf(".");
  if (dotIndex < 1 || KNOWN_DOMAINS.includes(domain)) return null;

  const tld = domain.slice(dotIndex + 1);
  const blocking = Boolean(TLD_TYPOS[tld]);
  const suggestedDomain =
    MISTAKEN_DOMAINS[domain] ||
    KNOWN_DOMAINS.find((known) => editDistance(domain, known) === 1) ||
    (blocking ? `${domain.slice(0, dotIndex)}.${TLD_TYPOS[tld]}` : null);

  return suggestedDomain
    ? { email: `${localPart}@${suggestedDomain}`, blocking }
    : null;
};
